import React from "react";
import { Box } from "@mui/material";
import cardPlaceholderImg from "../../Assets/images/property-card-claimed.webp";
import simplerHomeLogo from "../../Assets/images/logo.svg";
import {
  pickRandomMlsPhoto,
  type MlsPhotosContainer,
} from "../../Utils/mlsPlaceholder";

interface BlurredMlsPlaceholderProps {
  /** Property MLS payload (the slice with `mlsHasPhotoLists`). */
  mls?: MlsPhotosContainer | null;
  /** Stable seed — usually the property id. */
  seed?: string | number;
  /** Alt text — defaults to "Property" for screen readers. */
  alt?: string;
  /** Border radius applied to the wrapper (matches existing surfaces). */
  borderRadius?: number | string;
  /** Blur strength in px. Default 10 = noticeable softening, applied uniformly to
   * BOTH the random-MLS source AND the branded webp fallback so every card
   * placeholder reads the same way — even when the backend's
   * `mlsHasPhotoLists` happens to be empty.
   */
  blurPx?: number;
  /** Optional overlay (lock badge, CTA button, etc.) rendered on top. */
  children?: React.ReactNode;
  /** data-testid hook for tests. */
  testId?: string;
}

/**
 * A single static "placeholder" image for property cards / galleries.
 *
 * Source selection:
 *   1. Random MLS photo from the property's `mlsHasPhotoLists` (stable
 *      per propertyId via {@link pickRandomMlsPhoto})
 *   2. Fall back to the branded `cardPlaceholderImg` webp when no MLS
 *      photos exist
 *
 * Display: the chosen image is rendered with a CSS `blur` filter and a
 * slight scale-up so the blur halo never reveals the box edge. Children
 * are rendered as an overlay (e.g., the lock badge or "Add photos" CTA).
 *
 * Why blur the MLS: the raw MLS hero looked like a curated for-sale
 * listing photo, which is misleading for an unclaimed home in the
 * SimplerHome flow. Blurring keeps the visual variety / colour cue of
 * the actual property while making it clear this is a placeholder.
 */
const BlurredMlsPlaceholder: React.FC<BlurredMlsPlaceholderProps> = ({
  mls,
  seed = "",
  alt = "Property",
  borderRadius = 20,
  blurPx = 10,
  children,
  testId = "blurred-mls-placeholder",
}) => {
  const mlsUrl = pickRandomMlsPhoto(mls, seed);
  const usingMls = Boolean(mlsUrl);
  const src = mlsUrl || cardPlaceholderImg;

  return (
    <Box
      position="relative"
      sx={{
        width: "100%",
        height: "100%",
        borderRadius,
        overflow: "hidden",
      }}
      data-testid={testId}
      data-source={usingMls ? "mls" : "branded"}
    >
      <img
        src={src}
        alt={alt}
        // Plain inline styles (not MUI sx) so the filter/transform are
        // directly assertable in tests rather than hidden behind an
        // emotion class hash. Blur is applied to BOTH source types so a
        // listing of mixed-MLS / no-MLS properties looks visually
        // consistent. The scale-up prevents the blur halo from exposing
        // the wrapper background.
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          filter: `blur(${blurPx}px)`,
          transform: "scale(1.1)",
          transformOrigin: "center",
        }}
      />
      {/* SimplerHome wordmark — bottom-left, same on MLS + branded so
          the brand cue is consistent across placeholders. Sized small
          (subtle) but full opacity, sitting on a translucent white chip
          so it stays legible against any sky / roof tone underneath. */}
      <Box
        component="img"
        src={simplerHomeLogo}
        alt=""
        aria-hidden
        data-testid={`${testId}-wordmark`}
        sx={{
          position: "absolute",
          bottom: 10,
          left: 12,
          height: { xs: 14, md: 18 },
          width: "auto",
          padding: "3px 6px",
          borderRadius: 1.5,
          backgroundColor: "rgba(255, 255, 255, 0.88)",
          boxShadow: 1,
          pointerEvents: "none",
        }}
      />
      {children}
    </Box>
  );
};

export default BlurredMlsPlaceholder;
